import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Alert,
} from '@mui/material';
import { GET_INVITE_BY_TOKEN } from 'src/utils/gql/queries/companyInvite';
import { ACCEPT_INVITE } from 'src/utils/gql/mutations/companyInvite';
import { useSnackbar } from 'src/providers/snackbar/hook/useSnackbar';
import { setToken } from 'src/utils/localStorage';
import { ROUTE_SOURCES } from 'src/constants/routeSources';
import { AcceptInviteInput } from 'src/utils/gql/types';

const acceptInviteSchema = yup.object({
  password: yup
    .string()
    .min(8, 'Password must be at least 8 characters')
    .required('Password is required'),
  repeatPassword: yup
    .string()
    .oneOf([yup.ref('password')], 'Passwords do not match')
    .required('Confirm password is required'),
});

type AcceptInviteFormValues = {
  password: string;
  repeatPassword: string;
};

export const AcceptInvite: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const token = searchParams.get('token');

  const [inviteData, setInviteData] = useState<any>(null);

  const {
    data,
    loading: loadingInvite,
    error,
  } = useQuery(GET_INVITE_BY_TOKEN, {
    variables: { token },
    skip: !token,
    onCompleted: (data) => {
      setInviteData(data.getInviteByToken);
    },
    onError: (error) => {
      snackbar(error.message);
    },
  });

  const formMethods = useForm<AcceptInviteFormValues>({
    resolver: yupResolver(acceptInviteSchema),
    defaultValues: {
      password: '',
      repeatPassword: '',
    },
  });

  const [acceptInvite, { loading: accepting }] = useMutation(ACCEPT_INVITE, {
    onCompleted: (data) => {
      setToken(data.acceptInvite);
      snackbar('Account created successfully!', 'success');
      navigate(`/${ROUTE_SOURCES.CONNECTORS}`);
    },
    onError: (error) => {
      snackbar(error.message);
    },
  });

  const onSubmit: SubmitHandler<AcceptInviteFormValues> = (data) => {
    if (!token) return;

    acceptInvite({
      variables: {
        acceptData: {
          inviteToken: token,
          password: data.password,
        },
      },
    });
  };

  if (!token) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <Alert severity="error">Missing or invalid invite token</Alert>
      </Box>
    );
  }

  if (loadingInvite) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !inviteData) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <Alert severity="error">Invite not found or no longer valid</Alert>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      p={2}
    >
      <Card sx={{ maxWidth: 400, width: '100%' }}>
        <CardContent>
          <Typography variant="h5" gutterBottom textAlign="center">
            Accept Invite
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            mb={3}
            textAlign="center"
          >
            You have been invited to join{' '}
            <strong>{inviteData.company.name}</strong>
          </Typography>

          <Box component="form" onSubmit={formMethods.handleSubmit(onSubmit)}>
            <TextField
              fullWidth
              label="Email"
              value={inviteData.email}
              disabled
              margin="normal"
            />

            <TextField
              fullWidth
              label="First Name"
              value={inviteData.firstName}
              disabled
              margin="normal"
            />

            <TextField
              fullWidth
              label="Last Name"
              value={inviteData.lastName}
              disabled
              margin="normal"
            />

            <TextField
              fullWidth
              type="password"
              label="Password"
              {...formMethods.register('password')}
              error={!!formMethods.formState.errors.password}
              helperText={formMethods.formState.errors.password?.message}
              margin="normal"
            />

            <TextField
              fullWidth
              type="password"
              label="Confirm Password"
              {...formMethods.register('repeatPassword')}
              error={!!formMethods.formState.errors.repeatPassword}
              helperText={formMethods.formState.errors.repeatPassword?.message}
              margin="normal"
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={accepting}
              sx={{ mt: 2 }}
            >
              {accepting ? <CircularProgress size={24} /> : 'Create Account'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
